'use client';

import { format } from 'date-fns';
import FuzzySearch from 'fuzzy-search';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { IconArrowRight } from '@tabler/icons-react';

import { jakarta } from '@/lib/fonts';
import { cn, truncate } from '@/lib/utils';
import { Article } from '@/types/types';

export const BlogPostRows = ({
  articles,
  locale = 'fr',
}: {
  articles: Article[];
  locale?: string;
}) => {
  const [search, setSearch] = useState('');

  const searcher = new FuzzySearch(articles, ['title'], {
    caseSensitive: false,
  });

  const [results, setResults] = useState(articles);
  useEffect(() => {
    const results = searcher.search(search);
    setResults(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const isEn = locale === 'en';

  return (
    <div className="w-full py-12 md:py-16">
      <div className="flex sm:flex-row flex-col justify-between gap-4 items-center mb-10">
        <h3
          className={cn(
            jakarta.className,
            'text-2xl sm:text-3xl font-bold tracking-tight text-[#2e1452] dark:text-white'
          )}
        >
          {isEn ? 'All Articles' : 'Tous les articles'}
        </h3>
        <div className="relative w-full sm:w-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isEn ? 'Search an article...' : 'Rechercher un article...'}
            className="text-sm min-w-full sm:min-w-80 px-5 py-3 rounded-full bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800 focus:border-[#c02029] focus:ring-2 focus:ring-[#c02029]/20 focus:outline-none transition-all text-[#2e1452] dark:text-neutral-100 placeholder-neutral-400 shadow-sm"
          />
        </div>
      </div>

      <div className="space-y-4">
        {results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center rounded-3xl bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800">
            <p className="text-[#5e5873] dark:text-neutral-300 font-medium text-lg">
              {isEn ? `No article matches "${search}"` : `Aucun article ne correspond à "${search}"`}
            </p>
            <p className="text-[#50456c] dark:text-neutral-400 text-sm mt-2">
              {isEn ? 'Try searching with other keywords.' : 'Essayez avec d’autres mots-clés.'}
            </p>
          </div>
        ) : (
          results.map((article, index) => (
            <BlogPostRow article={article} locale={locale} key={article.slug + index} />
          ))
        )}
      </div>
    </div>
  );
};

export const BlogPostRow = ({
  article,
  locale = 'fr',
}: {
  article: Article;
  locale?: string;
}) => {
  return (
    <Link
      href={`/${locale}/blog/${article.slug}`}
      key={`${article.slug}`}
      className="flex md:flex-row flex-col items-start justify-between md:items-center group p-6 sm:p-8 bg-white dark:bg-neutral-900 rounded-3xl border border-[#e5e2f5] dark:border-neutral-800 hover:border-[#c02029]/40 shadow-md shadow-[#301754]/5 hover:shadow-xl hover:shadow-[#c02029]/10 transition-all duration-300 hover:-translate-y-0.5 gap-6"
    >
      <div className="flex-1">
        <div className="flex gap-2 items-center mb-3 flex-wrap">
          <time
            dateTime={article.publishedAt}
            className="text-xs font-medium text-[#50456c] dark:text-neutral-400"
          >
            {format(new Date(article.publishedAt), 'MMMM dd, yyyy')}
          </time>
          {article.categories && article.categories.length > 0 && (
            <>
              <span className="text-[#c02029] font-light">—</span>
              <div className="flex gap-1.5 flex-wrap">
                {article.categories?.map((category, idx) => (
                  <span
                    key={`category-${idx}`}
                    className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#f4f2fc] text-[#50456c] dark:bg-neutral-800 dark:text-neutral-300 capitalize tracking-wide border border-[#e5e2f5]"
                  >
                    {category.name}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <h4
          className={cn(
            jakarta.className,
            'text-[#2e1452] dark:text-white text-lg sm:text-xl font-bold group-hover:text-[#c02029] transition duration-200 leading-snug'
          )}
        >
          {article.title}
        </h4>
        <p className="text-[#5e5873] dark:text-neutral-300 text-sm sm:text-base mt-2 max-w-3xl leading-relaxed">
          {truncate(article.description, 140)}
        </p>
      </div>

      <span className="w-9 h-9 rounded-full bg-[#c02029] text-white flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-[#a61b23] shadow-md shadow-[#c02029]/25 flex-shrink-0 self-end md:self-center">
        <IconArrowRight className="w-4 h-4 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
};

export default BlogPostRows;
